import { Badge, BadgeShape, Image, type ImageProps, ScoreBar } from '@vizit/atoms';
import { BadgeLevel, BadgeSize } from '@vizit/atoms';
import { useShowGenAiBadge } from '@vizit/feature-flags';
import { colors, cva, cx } from '@vizit/theme';
import { hasScore, serializeScore } from '@vizit/utils';
import React from 'react';
import { AssetContext } from './asset.context-provider';

interface AssetImageProps extends ImageProps {
  /**
   * Fades and desaturates the tile while it waits on something, e.g. a scoring still running. Purely visual: say
   * what it waits on elsewhere when it matters
   * @default false
   */
  isDimmed?: boolean;
  overlay?: AssetImageOverlayProps;
}

interface AssetImageOverlayProps {
  src: string;
  alt: string;
  onError?: () => void;
}

const styles = {
  /**
   * GenAI badge marking AI-generated imagery: the blue diagonal gradient box
   * of the original design, now lettered "AI" in JetBrains Mono instead of the
   * image-spark glyph and sized by padding around the letters rather than a
   * fixed square. The inset ring stands in for the 1px top-lit border.
   */
  aiBadge: cva({
    base: [
      'absolute bottom-2 left-2 z-10 flex items-center justify-center px-3 py-2',
      'bg-gradient-to-tr from-blue-600 to-blue-400 ring-1 ring-inset ring-white/10',
      'font-mono text-sm font-medium leading-none text-white select-none',
    ],
  }),
  root: cva({
    base: 'inline-flex select-none flex-col gap-1 w-full',
    variants: {
      isSelected: {
        true: 'ring-2 ring-gray-modern-500 ring-offset-1 ring-offset-white',
        false: '',
      },
      isDimmed: {
        true: 'opacity-[0.45] grayscale-[60%]',
        false: '',
      },
    },
    defaultVariants: {
      isSelected: false,
      isDimmed: false,
    },
  }),
  imageContainer: cva({
    base: 'relative',
  }),
};

function AssetImageOverlay({ src, alt, onError, hasBorder }: AssetImageOverlayProps & { hasBorder?: boolean }) {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [hasFailed, setHasFailed] = React.useState(false);

  if (hasFailed) {
    return null;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cx(
        'absolute inset-0 h-full w-full object-cover',
        hasBorder && 'border border-primary',
        !isLoaded && 'opacity-0',
      )}
      onLoad={() => setIsLoaded(true)}
      onError={() => {
        setHasFailed(true);
        onError?.();
      }}
    />
  );
}

function AssetImage(imageProps: AssetImageProps) {
  const { className, name, src, isDimmed = false, overlay, ...otherImageProps } = imageProps;

  // score has been validated in the context provider
  const {
    isLoading: isLoadingContext,
    isUnscored,
    unscoredLabel,
    isScoreLoading,
    isViewOnly,
    score,
    isSelected,
    isHovered,
    showScoreBar = true,
    slotNumber,
    isAiGenerated = false,
    generatedChildCount,
  } = React.useContext(AssetContext);

  const showGenAiBadge = useShowGenAiBadge();

  const isLoading = isScoreLoading || isLoadingContext || (!hasScore(score) && !isUnscored);

  const showEmptyPlaceholder = isViewOnly && !src;

  const hasGeneratedChildren = typeof generatedChildCount === 'number' && generatedChildCount > 0;

  const badgeLabel = hasGeneratedChildren
    ? `AI-generated image, ${generatedChildCount} generated from it`
    : 'AI-generated image';

  return (
    <div className={cx(styles.root({ isSelected, isDimmed }), className)}>
      <div className={styles.imageContainer()}>
        {showEmptyPlaceholder ? (
          <div
            className="aspect-square w-full border border-primary bg-secondary-subtle"
            role="img"
            aria-label="Empty asset placeholder"
          >
            <svg className="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
              <line x1="0" y1="0" x2="100%" y2="100%" stroke={colors['gray-utility']['200']} strokeWidth="1" />
              <line x1="100%" y1="0" x2="0" y2="100%" stroke={colors['gray-utility']['200']} strokeWidth="1" />
            </svg>
          </div>
        ) : (
          <>
            <Image isUploading={isLoadingContext} name={name} src={src} contain={false} {...otherImageProps} />
            {overlay && <AssetImageOverlay key={overlay.src} {...overlay} hasBorder={otherImageProps.border} />}
            {slotNumber && (
              <div className="absolute top-1 left-1 z-10 flex">
                <Badge level={BadgeLevel.Slot} size={BadgeSize.Sm} shape={BadgeShape.Box}>
                  {slotNumber}
                </Badge>
              </div>
            )}
            {isAiGenerated && showGenAiBadge && (
              <div className={styles.aiBadge()} role="img" aria-label={badgeLabel}>
                <span aria-hidden="true">{hasGeneratedChildren ? `AI +${generatedChildCount}` : 'AI'}</span>
              </div>
            )}
          </>
        )}
      </div>
      {showScoreBar &&
        (showEmptyPlaceholder ? (
          <div className="h-[29px] w-full rounded-[1px] bg-primary-hover" />
        ) : (
          <ScoreBar
            score={hasScore(score) && !isUnscored ? Number(serializeScore(score)) : null}
            isHovered={isHovered && !isLoadingContext && !isScoreLoading}
            isLoading={isLoading}
            isUnscored={!!isUnscored}
            unscoredLabel={unscoredLabel}
          />
        ))}
    </div>
  );
}

export type { AssetImageOverlayProps, AssetImageProps };
export { AssetImage };
